import { getLinkFromEntityNode } from '@atlassian/twg-entity-node-utils';

import { getConfig } from '../../../../../config';

const isUrlString = (v: unknown): v is string => typeof v === 'string' && /^https?:\/\//i.test(v);

export const getSmartLinkUrl = (value: any): string | undefined => {
	if (value === null || value === undefined) {
		return undefined;
	}
	const { atlassianHomeUrl, defaultSiteCloudId, orgId } = getConfig();

	const firstAttempt = getLinkFromEntityNode(
		value,
		`${atlassianHomeUrl}/o/${orgId}`,
		defaultSiteCloudId,
	);
	if (firstAttempt !== null && firstAttempt.length !== 0) {
		return firstAttempt;
	}

	if (isUrlString(value)) {
		return value;
	}

	if (isUrlString(value?.url)) {
		return value.url;
	}

	if (isUrlString(value?.webUrl)) {
		return value.webUrl;
	}

	if (isUrlString(value?.href)) {
		return value.href;
	}

	// Check for links at the top level (e.g., normalized entities where
	// ConfluencePage_links has been stripped to just "links")
	if (value.links && typeof value.links === 'object' && !Array.isArray(value.links)) {
		const base = value.links.base;
		const webUi = value.links.webUi;

		if (typeof base === 'string' && typeof webUi === 'string') {
			const baseNormalized = base.replace(/\/$/, '');
			const webUiNormalized = webUi.replace(/^\//, '');
			const combined = `${baseNormalized}/${webUiNormalized}`;

			if (isUrlString(combined)) {
				return combined;
			}
		}
	}

	// Check for top-level fields ending in `_url` or `_webUrl`
	for (const topKey of Object.keys(value)) {
		if ((topKey.endsWith('_webUrl') || topKey.endsWith('_url')) && isUrlString(value[topKey])) {
			return value[topKey];
		}
	}

	// Check for top-level fields ending in `_links` with { base, webUi }
	for (const topKey of Object.keys(value)) {
		if (!topKey.endsWith('_links')) {
			continue;
		}
		const links = value[topKey];
		if (!links || typeof links !== 'object' || Array.isArray(links)) {
			continue;
		}
		const base = links.base;
		const webUi = links.webUi;
		if (typeof base === 'string' && typeof webUi === 'string') {
			const baseNormalized = base.replace(/\/$/, '');
			const webUiNormalized = webUi.replace(/^\//, '');
			const combined = `${baseNormalized}/${webUiNormalized}`;
			if (isUrlString(combined)) {
				return combined;
			}
		}
	}

	const data = value?.data;
	if (data && typeof data === 'object' && !Array.isArray(data)) {
		if (isUrlString((data as any).url)) {
			return (data as any).url;
		}

		if (isUrlString((data as any).webUrl)) {
			return (data as any).webUrl;
		}

		for (const dataKey of Object.keys(data)) {
			if (
				(dataKey.endsWith('_webUrl') || dataKey.endsWith('_url')) &&
				isUrlString((data as any)[dataKey])
			) {
				return (data as any)[dataKey];
			}
		}

		if (
			(data as any).links &&
			typeof (data as any).links === 'object' &&
			!Array.isArray((data as any).links)
		) {
			const base = (data as any).links.base;
			const webUi = (data as any).links.webUi;

			if (typeof base === 'string' && typeof webUi === 'string') {
				const baseNormalized = base.replace(/\/$/, '');
				const webUiNormalized = webUi.replace(/^\//, '');
				const combined = `${baseNormalized}/${webUiNormalized}`;

				if (isUrlString(combined)) {
					return combined;
				}
			}
		}

		// Fallback: look for any field ending in `_links` that contains a { base, webUi } pair
		for (const dataKey of Object.keys(data)) {
			if (!dataKey.endsWith('_links')) {
				continue;
			}

			const links = (data as any)[dataKey];
			if (!links || typeof links !== 'object' || Array.isArray(links)) {
				continue;
			}

			const base = links.base;
			const webUi = links.webUi;

			if (typeof base === 'string' && typeof webUi === 'string') {
				const baseNormalized = base.replace(/\/$/, '');
				const webUiNormalized = webUi.replace(/^\//, '');
				const combined = `${baseNormalized}/${webUiNormalized}`;

				if (isUrlString(combined)) {
					return combined;
				}
			}
		}
	}

	return undefined;
};

/**
 * Extracts a display name from a value object, checking for common name fields.
 * If the key contains an underscore, extracts the part after it and also checks for that field.
 * Checks in order: displayName, name, title, summary, and the extracted field name (if applicable).
 */
export const getDisplayName = (key: string, value: any): string | undefined => {
	if (!value || typeof value !== 'object' || Array.isArray(value)) {
		return undefined;
	}

	const underscoreIndex = key.indexOf('_');
	const extractedFieldName = underscoreIndex !== -1 ? key.substring(underscoreIndex + 1) : null;

	const priorityFields = ['displayName', 'name', 'title', 'summary'] as const;

	// Check direct fields first
	for (const field of priorityFields) {
		if ((value as any)[field] != null && typeof (value as any)[field] !== 'object') {
			return String((value as any)[field]);
		}
	}

	// Check nested data structure (e.g., entity.data.field)
	const data = (value as any).data;
	if (data && typeof data === 'object' && !Array.isArray(data)) {
		for (const field of priorityFields) {
			if ((data as any)[field] != null && typeof (data as any)[field] !== 'object') {
				return String((data as any)[field]);
			}
		}

		for (const field of priorityFields) {
			for (const dataKey of Object.keys(data)) {
				if (
					dataKey.endsWith(`_${field}`) &&
					(data as any)[dataKey] != null &&
					typeof (data as any)[dataKey] !== 'object'
				) {
					return String((data as any)[dataKey]);
				}
			}
		}

		if (
			extractedFieldName &&
			(data as any)[extractedFieldName] != null &&
			typeof (data as any)[extractedFieldName] !== 'object'
		) {
			return String((data as any)[extractedFieldName]);
		}
	}

	if (
		extractedFieldName &&
		(value as any)[extractedFieldName] != null &&
		typeof (value as any)[extractedFieldName] !== 'object'
	) {
		return String((value as any)[extractedFieldName]);
	}

	return undefined;
};
